<template>
  <div
    class="autocomplete"
    :class="{
      focused: focused,
      active: showList,
      loading: loading,
      invalid: invalid,
    }"
  >
    <div v-if="label" class="label hidden-label">
      {{ label }}
    </div>

    <div class="wrapper">
      <div class="field">
        <input v-model="localValue" type="text" @focus="onFocus" />

        <div v-if="localValue" class="clear">
          <svg-icon name="times" color="secondary" @click="clear"></svg-icon>
        </div>

        <div
          v-if="label"
          class="label visible-label"
          :class="{
            active: localValue,
          }"
        >
          {{ label }}
          <span v-if="requiredMark" class="required-mark">*</span>
        </div>

        <div class="lock" @click="openContainer"></div>
      </div>

      <div v-show="showList" ref="container" class="container">
        <div class="container__wrapper">
          <div class="search">
            <div class="search__icon">
              <svg width="13" height="13" viewBox="0 0 17 17" xmlns="http://www.w3.org/2000/svg" fill="none">
                <path
                  d="M11.4142 11L15.6569 15.2426M13 7C13 10.3137 10.3137 13 7 13C3.68629 13 1 10.3137 1 7C1 3.68629 3.68629 1 7 1C10.3137 1 13 3.68629 13 7Z"
                  stroke-width="2"
                  stroke="#bfc9e0"
                  stroke-linecap="round"
                />
              </svg>
            </div>

            <input ref="searchInput" v-model="query" type="text" class="search__input" placeholder="Поиск" />

            <transition name="fade">
              <div v-if="query" class="search__clear" @click="resetSearch">
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M0.325608 10.746C-0.0649162 10.3555 -0.0649162 9.72233 0.325608 9.33181L9.36312 0.292893C9.75364 -0.0976313 10.3868 -0.0976309 10.7773 0.292893C11.1679 0.683418 11.1679 1.31658 10.7773 1.70711L1.73982 10.746C1.3493 11.1365 0.716132 11.1365 0.325608 10.746Z"
                    fill="#FF238D"
                  />
                  <path
                    d="M10.6836 10.6835C11.0741 10.293 11.0741 9.65986 10.6836 9.26933L1.70711 0.292893C1.31658 -0.0976313 0.683417 -0.0976309 0.292893 0.292893C-0.0976311 0.683418 -0.0976311 1.31658 0.292893 1.70711L9.26935 10.6835C9.65987 11.0741 10.293 11.0741 10.6836 10.6835Z"
                    fill="#FF238D"
                  />
                </svg>
              </div>
            </transition>
          </div>

          <div v-if="!loading && !list.length" class="empty">Ничего не найдено</div>

          <div class="list">
            <div v-for="item in list" :key="item.id" class="list-item" @click="select(item)">
              {{ item.text }}
            </div>
          </div>

          <loader v-if="loading" :active="loading" class="loader" />

          <div class="actions">
            <c-button class="actions__cancel" size="small" label="Отмена" @click="close" />
          </div>
        </div>
      </div>
    </div>

    <error-feedback v-if="error" :text="error" />
  </div>
</template>

<script>
import SvgIcon from "@/components/svg-icon";
import Loader from "@/components/loader";
import CButton from "@/components/controls2/Button";
import ErrorFeedback from "@/components/error-feedback";
import { debounce } from "@/utils/timer/debounce";

let currentRequestId = 0;

export default {
  components: { SvgIcon, Loader, CButton, ErrorFeedback },
  props: ["value", "getList", "label", "requiredMark", "invalid", "error"],
  data() {
    return {
      localValue: "",
      query: "",
      list: [],
      loading: false,
      showList: false,
      focused: false,
    };
  },
  watch: {
    localValue(v) {
      if (!this.focused) {
        if (v !== this.value) this.$emit("input", v);
        return;
      }

      this.loading = true;
      this.updateList(v);
      this.showList = true;

      if (v === this.value) return;
      this.$emit("input", v);
    },
    query(v) {
      this.loading = true;
      this.updateList(v);
      this.showList = true;
    },
    value(v) {
      if (v === this.localValue) return;
      this.localValue = v;
    },
  },
  created() {
    this.localValue = this.value;
    this._cb = (e) => {
      if (this.$el.contains(e.target)) return;
      this.close();
    };
    document.addEventListener("click", this._cb);
  },
  beforeDestroy() {
    document.removeEventListener("click", this._cb);
  },
  methods: {
    onFocus() {
      this.focused = true;
      if (this.localValue) {
        this.loading = true;
        this.showList = true;
        this.updateList(this.localValue);
      }
    },
    select(item) {
      this.localValue = item.text;
      this.close();
    },
    updateList: debounce(async function updateList(v) {
      currentRequestId += 1;
      this.loading = true;
      const requestId = currentRequestId;
      const list = await this.getList(v);
      if (requestId === currentRequestId) {
        this.list = list || [];
        this.loading = false;
      }
    }, 500),
    openContainer() {
      this.query = this.localValue;

      this.showList = true;
      this.focused = true;
      this.list = [];
    },
    close() {
      this.focused = false;
      this.showList = false;
      this.list = [];
    },
    clear() {
      this.localValue = "";
      this.close();
    },
    resetSearch() {
      this.query = "";
    },
  },
};
</script>

<style lang="scss" scoped>
@import "~@/assets/styles/vars";
@import "~@/assets/styles/decoration";

.autocomplete {
  position: relative;

  .wrapper {
    position: relative;
  }

  .mask {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    cursor: pointer;
  }

  .field {
    position: relative;
    width: 100%;
    display: flex;
    cursor: pointer;

    .lock {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;

      @include bootstrapMd {
        display: none;
      }
    }

    input {
      width: 100%;
      border: none;
      outline: none;
      text-overflow: ellipsis;
      padding: 0 40px 10px 0;
      min-height: 30px;
      font: inherit;
      font-size: 15px;
      line-height: 1.2;

      border-bottom: 1px solid #bfc9e0;
    }

    .clear {
      position: absolute;
      right: 5px;
      bottom: 12px;
      color: $color-secondary;
      cursor: pointer;
    }
  }

  .container {
    position: absolute;
    top: 100%;
    margin-top: 10px;
    left: 0;
    right: 0;
    width: 350px;
    min-width: 100%;
    max-width: 100%;
    min-height: 50px;
    z-index: 100;
    background: white;
    box-shadow: 0px 4px 16px rgba(0, 15, 38, 0.25);
    border-radius: 6px;
    padding: 10px;

    .empty {
      color: $color-label;
      font-size: 15px;
      min-height: 30px;
      display: flex;
      align-items: center;
    }

    .list {
      @include scrollbar;
      max-height: 300px;
      width: 100%;
      overflow: auto;
    }

    .list-item {
      font-size: 15px;
      min-height: 30px;
      padding: 4px 14px;
      display: flex;
      align-items: center;
      cursor: pointer;
      border-radius: 15px;
      line-height: 1.2;
      &:hover {
        background: #e1edff;
      }
    }

    .loader {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      height: 100%;

      /deep/ {
        .loader-overlay {
          height: 100% !important;
          max-height: 100%;
          border: none;
          background: rgba(255, 255, 255, 0.6);
        }
        .loader-icon {
          font-size: 24px;
        }
      }
    }

    .search, .actions {
      display: none;
    }

    @include beforeBootstrapMd {
      position: fixed;
      top: 10px;
      left: 10px;
      right: 10px;
      bottom: 10px;
      z-index: 500;
      min-width: 0;
      width: calc(100% - 20px);

      &__wrapper {
        display: flex;
        flex-direction: column;
        height: 100%;
      }

      .list {
        flex-grow: 1;
        max-height: none;
      }

      .actions {
        display: block;
        padding: 10px 14px 0;
      }

      .search {
        display: block;
        flex-shrink: 0;
        display: flex;
        align-items: flex-end;
        position: relative;
        width: calc(100% - 30px);

        padding: 10px 0 8px;
        margin: 0 15px 13px 15px;
        border-bottom: 1px solid $color-border;

        &__icon {
          margin-right: 10px;
          font-size: 0;
          margin-bottom: 2px;

          svg {
            stroke: $color-label;
            fill: none;
          }
        }

        &__input {
          border: none;
          height: 17px;
          font-size: 13px;
          flex: 1;
          width: inherit;

          &:focus {
            outline: none;
          }

          &::placeholder {
            font-style: italic;
            color: $color-label;
          }
        }

        &__clear {
          cursor: pointer;
          transition: 0.2s;
          margin-left: 5px;

          &_hidden {
            opacity: 0;
            visibility: hidden;
          }
        }
      }

      .loader {
        top: 55px;
        bottom: 55px;

        height: auto;
      }
    }
  }

  .label {
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden !important;
    max-width: 100%;
    display: flex;
    align-items: center;
    line-height: 13px;
    color: $color-label;

    .required-mark {
      color: red;
    }
  }

  .visible-label {
    position: absolute;
    left: 0;
    pointer-events: none;
    padding-bottom: 10px;
    transition: all 200ms;
    font: 400 15px Fira Sans;

    &.active {
      font-size: 12px;
      transform: translate3d(0, -100%, 0);
    }
  }

  .hidden-label {
    font-size: 12px;
    visibility: hidden;
    margin-bottom: 10px;
  }

  &.invalid {
    .field {
      input {
        border-color: $color-secondary;
      }
    }
  }

  &.focused {
    .field {
      input {
        border-color: #0066ff;
      }
    }
  }

  &.loading {
    .container {
    }
  }
}
</style>
